"use client";
import { useEffect, useRef } from "react";
import { Socket } from "socket.io-client";

interface NewMessage {
  from?: string;
  body?: string;
  timestamp?: number;
  fromMe?: boolean;
  type?: string;
  users_mentioned?: unknown[];
  media?: unknown;
  replyBy?: unknown;
  id?: string;
  serializedId?: string;
  quotedMsg?: unknown;
}

interface Chat {
  id?: string;
  contactName?: string;
  name?: string;
  subject?: string;
  pictureUrl?: string;
  ticket?: Ticket;
  users_mentioned?: unknown[];
  replyBy?: unknown;
  lastMessage?: string;
  lastMessageType?: string;
  timestamp?: number;
  unreadCount?: number;
  ticketId?: string | number;
  quotedMsg?: unknown;
  chats?: Contact[];
  state?: string;
}

interface Ticket {
  id?: string | number;
  status?: string;
}

interface Contact {
  id?: string;
  contactName?: string;
  name?: string;
  subject?: string;
  pictureUrl?: string;
  ticket?: Ticket;
  ticketId?: string | number;
  users_mentioned?: unknown[];
  lastMessage?: string;
  lastMessageType?: string;
  timestamp?: number;
  unreadCount?: number;
  media?: unknown;
  quotedMsg?: unknown;
  replyBy?: unknown;
}

interface Message {
  id?: string;
  from?: string;
  body?: string;
  timestamp?: number;
  fromMe?: boolean;
  type?: string;
  users_mentioned?: unknown[];
  media?: unknown;
  replyBy?: unknown;
  serializedId?: string;
  ticketId?: string | number;
  quotedMsg?: unknown;
}

export const useInboxChannel = (
  socket: Socket | null,
  supplierUuid: string,
  setQrCode: (qr: string | null) => void,
  setContacts: (updater: Contact[] | ((prev: Contact[]) => Contact[])) => void,
  setIsQRVisible: (visible: boolean) => void,
  setMessages: (updater: Message[] | ((prev: Message[]) => Message[])) => void,
  setSelectedContact: (contact: string | null) => void,
  selectedContact: string | null,
  setSelectedName: (name: string | null) => void,
  setIsLoading: (loading: boolean) => void,
  messageContent: string,
  setMessageContent: (content: string) => void,
  updateMessages: (data: unknown) => void,
  ticket: Ticket | null,
  setIsLoadingMessages: (loading: boolean) => void,
  contactsClosed: Contact[],
  setContactsClosed: (
    updater: Contact[] | ((prev: Contact[]) => Contact[]),
  ) => void,
  contactsSnoozed: Contact[],
  setContactsSnoozed: (
    updater: Contact[] | ((prev: Contact[]) => Contact[]),
  ) => void,
  setUsersLive: (users: unknown[]) => void,
  setTokenTwilio: (token: string) => void,
  setSearchResults: (results: Contact[]) => void,
  setIsLoadingSearch: (loading: boolean) => void,
  suffix: string | null = null,
  setShowButtonQR: (show: boolean) => void = () => {},
  userId: string | number | null | undefined,
) => {
  // Refs so socket handlers always read the latest values without stale closures.
  const selectedContactRef = useRef(selectedContact);
  const ticketRef = useRef(ticket);
  const contactsSnoozedRef = useRef(contactsSnoozed);
  const pendingLoadCountRef = useRef(0);

  selectedContactRef.current = selectedContact;
  ticketRef.current = ticket;
  contactsSnoozedRef.current = contactsSnoozed;

  const uuidWithSuffix = suffix ? `${supplierUuid}-${suffix}` : supplierUuid;

  // ── Main socket event handlers ─────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    socket.on(`states-messages-${uuidWithSuffix}`, async (data: unknown) => {
      updateMessages(data);
    });

    socket.on(`qr-${uuidWithSuffix}`, async (data: { qr?: string }) => {
      if (data.qr) {
        setShowButtonQR(true);
        setQrCode(data.qr);
      } else {
        setIsQRVisible(false);
        setQrCode(null);
        setShowButtonQR(false);
      }
    });

    socket.on(`ready-${uuidWithSuffix}`, async (data: Chat) => {
      setIsQRVisible(false);
      setIsLoading(data.state === "LOADING");
      if (data.chats && data.chats.length > 0) {
        setContacts(
          data.chats.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0)),
        );
      } else {
        setContacts([]);
      }
    });

    socket.on(`disconnected-${uuidWithSuffix}`, async () => {
      setIsQRVisible(true);
      setContacts([]);
      setMessages([]);
      setSelectedContact(null);
      setSelectedName(null);
    });

    socket.on(
      `new-message-${uuidWithSuffix}`,
      async (data: {
        newMessage: NewMessage;
        chat: Chat;
        newChat?: boolean;
        unreadCount?: number;
      }) => {
        const isNoteMessage = data?.newMessage?.type === "note";
        const currentSelectedContact = selectedContactRef.current;
        const isCurrentChat =
          data.chat.id === currentSelectedContact ||
          (typeof window !== "undefined" &&
            decodeURIComponent(window.location.pathname).endsWith(
              `/${data.chat.id}`,
            ));

        setContacts((prevContacts) => {
          let updatedContacts: Contact[];

          if (data.newChat) {
            const alreadyExists = prevContacts.some(
              (c) => c.id === data?.chat?.id,
            );
            if (alreadyExists) {
              updatedContacts = prevContacts.map((contact) =>
                contact.id === data.chat.id
                  ? {
                      ...contact,
                      lastMessage: isNoteMessage
                        ? contact.lastMessage
                        : data?.newMessage?.body,
                      lastMessageType: isNoteMessage
                        ? contact.lastMessageType
                        : data?.newMessage?.type,
                      timestamp: data?.newMessage?.timestamp,
                      unreadCount: data?.newMessage?.fromMe
                        ? 0
                        : (contact.unreadCount ?? 0) + 1,
                      ticket: data?.chat?.ticket,
                      users_mentioned: data?.chat?.users_mentioned,
                      ticketId: data?.chat?.ticket?.id,
                      quotedMsg: data?.newMessage?.quotedMsg,
                    }
                  : contact,
              );
            } else {
              updatedContacts = [
                {
                  id: data?.chat?.id,
                  lastMessage: isNoteMessage ? "" : data?.newMessage?.body,
                  lastMessageType: isNoteMessage
                    ? undefined
                    : data?.newMessage?.type,
                  timestamp: data?.newMessage?.timestamp,
                  contactName: data?.chat?.contactName,
                  unreadCount: data?.newMessage?.fromMe
                    ? 0
                    : (data?.unreadCount ?? 0) + 1,
                  name: data?.chat?.name,
                  subject: data?.chat?.subject,
                  pictureUrl: data?.chat?.pictureUrl,
                  ticket: data?.chat?.ticket,
                  users_mentioned: data?.chat?.users_mentioned,
                  ticketId: data?.chat?.ticket?.id,
                  media: data?.newMessage?.media,
                  quotedMsg: data?.newMessage?.quotedMsg,
                },
                ...prevContacts,
              ];
            }
            return updatedContacts.sort(
              (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0),
            );
          } else {
            updatedContacts = prevContacts.map((contact) => {
              if (contact.id === data.chat.id) {
                return {
                  ...contact,
                  lastMessage: isNoteMessage
                    ? contact.lastMessage
                    : data.newMessage.body,
                  lastMessageType: isNoteMessage
                    ? contact.lastMessageType
                    : data.newMessage.type,
                  timestamp: data.newMessage.timestamp,
                  unreadCount: data.newMessage.fromMe || isCurrentChat ? 0 : (contact.unreadCount ?? 0) + 1,
                  ticket: data?.chat?.ticket,
                  users_mentioned: data?.chat?.users_mentioned,
                  ticketId: data?.chat?.ticket?.id,
                  quotedMsg: data?.newMessage?.quotedMsg,
                };
              }
              return contact;
            });

            const contactIndex = updatedContacts.findIndex(
              (contact) => contact.id === data.chat.id,
            );

            if (contactIndex !== -1) {
              const [updatedContact] = updatedContacts.splice(contactIndex, 1);
              updatedContacts.unshift(updatedContact);
            } else {
              updatedContacts.unshift({
                id: data.chat.id,
                lastMessage: isNoteMessage ? "" : data.newMessage.body,
                lastMessageType: isNoteMessage
                  ? undefined
                  : data.newMessage.type,
                timestamp: data.newMessage.timestamp,
                contactName: data.chat.contactName,
                unreadCount:
                  data.newMessage.fromMe || isCurrentChat
                    ? 0
                    : Math.max(data.chat.unreadCount ?? 0, 1),
                name: data?.chat?.name,
                subject: data?.chat?.subject,
                pictureUrl: data?.chat?.pictureUrl,
                ticket: data?.chat?.ticket,
                users_mentioned: data?.chat?.users_mentioned,
                replyBy: data?.chat?.replyBy,
                ticketId: data?.chat?.ticket?.id,
                quotedMsg: data?.newMessage?.quotedMsg,
              });
              setContactsClosed((prevContactsClosed) => {
                const exists = prevContactsClosed.some(
                  (contact) => contact.ticket?.id === data.chat.ticket?.id,
                );
                if (exists) {
                  return prevContactsClosed.filter(
                    (contact) => contact.ticket?.id !== data.chat.ticket?.id,
                  );
                }
                return prevContactsClosed;
              });
              if (contactsSnoozedRef.current.length > 0) {
                setContactsSnoozed((prevContactsSnoozed) => {
                  const exists = prevContactsSnoozed?.some(
                    (contact) => contact.ticket?.id === data.chat.ticket?.id,
                  );
                  if (exists) {
                    return prevContactsSnoozed.filter(
                      (contact) => contact.ticket?.id !== data.chat.ticket?.id,
                    );
                  }
                  return prevContactsSnoozed;
                });
              }
            }
          }

          return updatedContacts.sort(
            (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0),
          );
        });

        if (isCurrentChat) {
          socket.emit(`chat-mark-read-${uuidWithSuffix}`, {
            ticket: ticketRef.current,
          });
        }

        if (isCurrentChat) {
          const incomingMsg: Message = {
            from: data.newMessage.from,
            body: data.newMessage.body,
            timestamp: data.newMessage.timestamp,
            fromMe: data.newMessage.fromMe,
            type: data.newMessage.type,
            users_mentioned: data.newMessage.users_mentioned,
            media: data.newMessage.media,
            replyBy: data.newMessage.replyBy,
            id: data.newMessage.id,
            serializedId: data.newMessage.serializedId,
            ticketId: data?.chat?.ticket?.id,
            quotedMsg: data?.newMessage?.quotedMsg,
          };
          setMessages((prevMessages) => {
            if (data.newMessage.fromMe) {
              let tempRemoved = false;
              let removedTemp: Message | undefined;
              const withoutOldestTemp = prevMessages.filter((m) => {
                if (!tempRemoved && String(m.id || "").startsWith("temp-")) {
                  tempRemoved = true;
                  removedTemp = m as Message;
                  return false;
                }
                return true;
              });
              // Preserve fields from the temp message if the server echo omits them
              const finalMsg: Message = {
                ...incomingMsg,
                ...(incomingMsg.quotedMsg == null && removedTemp?.quotedMsg != null
                  ? { quotedMsg: removedTemp.quotedMsg }
                  : {}),
                ...(incomingMsg.replyBy == null && (removedTemp as any)?.replyBy != null
                  ? { replyBy: (removedTemp as any).replyBy }
                  : {}),
                ...((incomingMsg as any).authorName == null && (removedTemp as any)?.authorName != null
                  ? { authorName: (removedTemp as any).authorName }
                  : {}),
              };
              return [...withoutOldestTemp, finalMsg];
            }
            return [...prevMessages, incomingMsg];
          });
        }
      },
    );

    socket.on(
      `call-token-${uuidWithSuffix}-${userId}`,
      async (data: { token: string }) => {
        setTokenTwilio(data.token);
      },
    );

    socket.on(
      `load-tickets-${uuidWithSuffix}`,
      async (data: { tickets?: Contact[] }) => {
        if (data.tickets && data.tickets.length > 0) {
          setContacts((prevContacts) => {
            const existingIds = new Set(prevContacts.map((c) => c.id));
            const incoming = data.tickets!.filter(
              (t) => !existingIds.has(t.id),
            );
            if (incoming.length === 0) return prevContacts;
            return [...prevContacts, ...incoming].sort(
              (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0),
            );
          });
        }
      },
    );

    socket.on(
      `load-messages-${uuidWithSuffix}`,
      async (data: { messages?: Message[] }) => {
        const incoming = data.messages || [];
        console.log("messages1234" , data);
        

        if (!incoming.length) {
          if (pendingLoadCountRef.current > 0) {
            pendingLoadCountRef.current -= 1;
          }
          setIsLoadingMessages(false);
          return;
        }

        const currentTicket = ticketRef.current;
        const belongsToCurrentTicket =
          currentTicket &&
          incoming.some((m) => m.ticketId === currentTicket.id);

        if (pendingLoadCountRef.current === 0 && !belongsToCurrentTicket)
          return;

        if (pendingLoadCountRef.current > 0) {
          pendingLoadCountRef.current -= 1;
        }

        setMessages((prevMessages) => {
          const withoutTemps = prevMessages.filter(
            (m) => !String(m.id || "").startsWith("temp-"),
          );
          const existingIds = new Set(withoutTemps.map((m) => m.id));
          const uniqueIncoming = incoming.filter((m) => !existingIds.has(m.id));
          return [...uniqueIncoming, ...withoutTemps];
        });

        setIsLoadingMessages(false);
      },
    );

    if (suffix) {
      socket.on(`load-messages-${supplierUuid}`, async () => {});
    }

    socket.on(
      `change-tickets-status-${uuidWithSuffix}`,
      async (data: { ticket: Contact }) => {
        if (data.ticket.ticket?.status === "OPEN") {
          setContacts((prevContacts) => {
            const exists = prevContacts.some(
              (contact) => contact?.ticket?.id === data?.ticket?.ticket?.id,
            );
            if (!exists) {
              const updatedContacts = [...prevContacts, data.ticket];
              return updatedContacts.sort(
                (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0),
              );
            }
            return prevContacts;
          });
        } else if (data.ticket.ticket?.status === "CLOSED") {
          setContactsClosed((prevContactsClosed) => {
            const exists = prevContactsClosed.some(
              (contact) => contact?.ticket?.id === data?.ticket?.ticket?.id,
            );
            if (!exists) {
              const updatedContactsClosed = [
                ...prevContactsClosed,
                data.ticket,
              ];
              return updatedContactsClosed.sort(
                (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0),
              );
            }
            return prevContactsClosed;
          });
        }
      },
    );

    socket.on(
      `search-results-${uuidWithSuffix}`,
      async (data: { tickets?: Contact[] }) => {
        setIsLoadingSearch(false);
        setSearchResults(data?.tickets || []);
      },
    );

    socket.on(
      `unread-count-${uuidWithSuffix}`,
      async (data: { chat: Contact }) => {
        setContacts((prevContacts) =>
          prevContacts.map((contact) => {
            if (contact.id === data.chat.id) {
              return {
                ...contact,
                lastMessage: data.chat.lastMessage,
                unreadCount: data.chat.unreadCount,
                timestamp: data.chat.timestamp,
              };
            }
            return contact;
          }),
        );
      },
    );

    socket.on(
      `message-revoke-${uuidWithSuffix}`,
      async (data: { revokedId?: string; messageId?: string }) => {
        setMessages((prevMessages) =>
          prevMessages.map((m) =>
            m.id === data.revokedId || m.id === data.messageId
              ? { ...m, type: "revoked" }
              : m,
          ),
        );
      },
    );

    return () => {
      socket.off(`qr-${uuidWithSuffix}`);
      socket.off(`ready-${uuidWithSuffix}`);
      socket.off(`new-message-${uuidWithSuffix}`);
      socket.off(`disconnected-${uuidWithSuffix}`);
      socket.off(`call-token-${uuidWithSuffix}-${userId}`);
      socket.off(`unread-count-${uuidWithSuffix}`);
      socket.off(`load-tickets-${uuidWithSuffix}`);
      socket.off(`load-messages-${uuidWithSuffix}`);
      socket.off(`change-tickets-status-${uuidWithSuffix}`);
      socket.off(`search-results-${uuidWithSuffix}`);
      socket.off(`states-messages-${uuidWithSuffix}`);
      socket.off(`message-revoke-${uuidWithSuffix}`);
    };
  }, [socket, uuidWithSuffix]);

  // ── Active-users dynamic event ─────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const eventName = `active-users-chats-${selectedContact}-${uuidWithSuffix}`;

    const handler = (data: { users?: unknown[] }) =>
      setUsersLive(data.users || []);
    socket.on(eventName, handler);

    socket.emit(`active-users-chats-${uuidWithSuffix}`, {
      chatId: selectedContact,
    });

    return () => {
      socket.off(eventName, handler);
    };
  }, [socket, uuidWithSuffix, selectedContact]);

  const filesToMedia = async (
    files: File[],
  ): Promise<{ data: string; mimetype: string; filename: string }[]> => {
    const promises = files.map(
      (file) =>
        new Promise<{ data: string; mimetype: string; filename: string }>(
          (resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(",")[1];
              resolve({
                data: base64,
                mimetype: file.type,
                filename: file.name,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          },
        ),
    );
    return Promise.all(promises);
  };

  const sendMessage = async (
    attachments: File[],
    setAttachments: (files: File[]) => void = () => {},
    quotedMessageId: string | null = null,
  ): Promise<void> => {
    if (messageContent.trim() !== "" || attachments.length > 0) {
      console.log("Sending message with content:", messageContent , ticket , quotedMessageId);
      socket!.emit(`send-message-${uuidWithSuffix}`, {
        ticket: ticket,
        body: messageContent,
        attachments: await filesToMedia(attachments),
        ...(quotedMessageId ? { quotedMessageId } : {}),
      });
      setMessageContent("");
      setAttachments([]);
    }
  };

  const deleteMessage = async (
    ticket: Ticket | null,
    serializedId: string,
  ): Promise<void> => {
    if (serializedId && serializedId.trim() !== "") {
      socket!.emit(`delete-message-${uuidWithSuffix}`, {
        ticket: ticket,
        messageId: String(serializedId),
      });
    }
  };

  const loadMessages = (ticket: Ticket | null): void => {
    if (!supplierUuid || !ticket) return;
    pendingLoadCountRef.current += 1;
    socket!.emit(`request-messages-${uuidWithSuffix}`, { ticket: ticket });
  };

  const searchTickets = (searchParams: unknown): void => {
    setIsLoadingSearch(true);
    socket!.emit(`search-tickets-${uuidWithSuffix}`, { searchParams });
  };

  return { sendMessage, loadMessages, searchTickets, deleteMessage };
};
